import { useCallback, useId, useMemo } from "react";
import {
    isCloseFromDescendantLevel,
    type PickerChainContextValue,
} from "./PickerChainContext";
import type { PickerItemRegistration } from "./PickerContext";
import { useLatestCallback } from "./usePickerStable";
import type {
    PickerCellEditor,
    PickerFlyoutAutoOpen,
    PickerFlyoutOptions,
    PickerFlyoutQueryScope,
    PickerItemValue,
    PickerRootKind,
} from "./Picker.types";

export interface UsePickerChainValueOptions {
    /** Set for flyout levels: the chain of the top root this level commits into. */
    chain?: PickerChainContextValue;
    depth: number;
    kind: PickerRootKind;
    isMultiple: boolean;
    selectedValues: readonly PickerItemValue[];
    setSelectedValues: (values: readonly PickerItemValue[]) => void;
    registerItem: (registration: PickerItemRegistration) => void;
    /** Replaces the default top-root commit (`allowCustomValue` writes the leaf label as text). */
    onLeafCommit?: (values: readonly PickerItemValue[]) => void;
    container?: HTMLElement;
    flyoutDefaults?: PickerFlyoutOptions;
    flyoutQueryScope: PickerFlyoutQueryScope;
    flyoutAutoOpen: PickerFlyoutAutoOpen;
    isAutoHighlight: boolean;
    cellEditor?: PickerCellEditor;
    handleOpenChange: (open: boolean) => void;
    closePopup: () => void;
}

/**
 * The cascading-chain side of a list root: the chain value a top root
 * provides (or the parent chain a level forwards), and the Base UI open
 * handler that keeps this level open when the close request comes from a
 * descendant level's popup.
 */
export const usePickerChainValue = ({
    chain,
    depth,
    kind,
    isMultiple,
    selectedValues,
    setSelectedValues,
    registerItem,
    onLeafCommit,
    container,
    flyoutDefaults,
    flyoutQueryScope,
    flyoutAutoOpen,
    isAutoHighlight,
    cellEditor,
    handleOpenChange,
    closePopup,
}: UsePickerChainValueOptions) => {
    // Base UI reports a press, focus move or Escape inside a descendant flyout
    // as a reason to close this level; the chain marks those popups so the
    // request can be cancelled and the level stays open.
    const chainId = useId();
    const commitLeaf = useLatestCallback(onLeafCommit);
    const handleBaseOpenChange = useCallback(
        (
            nextOpen: boolean,
            details: { reason: string; event: Event; cancel: () => void },
        ) => {
            if (
                !nextOpen &&
                isCloseFromDescendantLevel({
                    reason: details.reason,
                    event: details.event,
                    chainId: chain?.chainId ?? chainId,
                    depth,
                })
            ) {
                details.cancel();
                return;
            }
            handleOpenChange(nextOpen);
        },
        [chain?.chainId, chainId, depth, handleOpenChange],
    );
    const chainValue = useMemo<PickerChainContextValue>(
        () =>
            chain ?? {
                chainId,
                kind,
                isMultiple,
                selectedValues,
                commitValues: (values) => {
                    if (commitLeaf) {
                        commitLeaf(values);
                        return;
                    }
                    // Leaves rendered in a level register into this root
                    // through the chain, so ownership filtering keeps them.
                    setSelectedValues(values);
                },
                registerItem,
                closeAll: closePopup,
                container,
                flyoutDefaults,
                flyoutQueryScope,
                flyoutAutoOpen,
                isAutoHighlight,
                cellEditor,
            },
        [
            chain,
            chainId,
            kind,
            isMultiple,
            selectedValues,
            commitLeaf,
            setSelectedValues,
            registerItem,
            closePopup,
            container,
            flyoutDefaults,
            flyoutQueryScope,
            flyoutAutoOpen,
            isAutoHighlight,
            cellEditor,
        ],
    );

    return { handleBaseOpenChange, chainValue };
};
